// dashboard data-completeness section, framed around ACHIEVEMENT: the
// hero band is the cumulative, only-ever-growing totals (RA requirements
// met, inventories done, Word RAs mapped, assessments ingested) plus the
// weekly momentum line; the honest ratio (94 of 1115, ~8%) stays on the
// board but demoted to a secondary panel with a three-segment
// done / in-progress / not-started breakdown so partial work reads as
// progress, not failure. every number is the API's real number — the
// reframing is which one leads, never what a number means.
//
// animation approach: CSS transitions armed one frame after mount (rAF)
// so bars sweep from 0; the big totals count up with an eased rAF loop.
// charts are recharts with the default draw-in.

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Area, AreaChart, CartesianGrid,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import { RefreshCw, TrendingUp, Database, Layers, Ship, Library, FileCheck2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  fetchDashboardStats,
  type DashboardStats,
  type StatsByType,
  type StatsLeaderboardRow,
} from '@/lib/dashboardStatsApi'

// ─── animation primitives ─────────────────────────────────────────────────

// eased count-up for the big totals
function useCountUp(target: number, durationMs = 1200): number {
  const [val, setVal] = useState(0)
  useEffect(() => {
    let raf = 0
    const t0 = performance.now()
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / durationMs)
      const eased = 1 - Math.pow(1 - p, 3) // easeOutCubic
      setVal(target * eased)
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, durationMs])
  return val
}

// arms a 0→value CSS transition one frame after mount
function useArmed(): boolean {
  const [armed, setArmed] = useState(false)
  useEffect(() => {
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setArmed(true)))
    return () => cancelAnimationFrame(raf)
  }, [])
  return armed
}

// ─── achievement tile (count-up total that only ever grows) ───────────────

function BigStat({ value, label, accent }: { value: number; label: string; accent?: boolean }) {
  const shown = useCountUp(value)
  return (
    <div className="min-w-[7rem]">
      <div className={`text-2xl font-semibold tabular-nums tracking-tight ${
        accent ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-900 dark:text-white'
      }`}>
        {Math.round(shown)}
      </div>
      <div className="text-[10px] uppercase leading-tight text-slate-400">{label}</div>
    </div>
  )
}

// ─── coverage bar (informational source presence) ─────────────────────────

const BAR_TONES: Record<StatsByType['key'], string> = {
  ra:        'bg-emerald-500 dark:bg-emerald-400',
  inventory: 'bg-emerald-500 dark:bg-emerald-400',
  word:      'bg-blue-500 dark:bg-blue-400',
  excel:     'bg-blue-500 dark:bg-blue-400',
}

function CoverageBar({ item, index, armed, partial }: {
  item: StatsByType; index: number; armed: boolean
  // extra amber segment (e.g. RA partial) rendered after the done segment
  partial?: number
}) {
  const { t } = useTranslation()
  const donePct = item.total > 0 ? (item.n / item.total) * 100 : 0
  const partialPct = partial && item.total > 0 ? (partial / item.total) * 100 : 0
  return (
    <div>
      <div className="mb-0.5 flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
          {t(`dashboard.growth.coverage.${item.key}`)}
        </span>
        <span className="text-[10px] tabular-nums text-slate-400">
          <span className="font-semibold text-slate-600 dark:text-slate-300">{item.n.toLocaleString()}</span>
          {' '}{t('dashboard.growth.completed')} · {item.pct.toFixed(0)}%
          {partial ? <> · <span className="text-amber-600 dark:text-amber-500">+{partial} {t('dashboard.growth.underway')}</span></> : null}
        </span>
      </div>
      <div className="h-1.5 flex overflow-hidden rounded-full bg-slate-200/80 dark:bg-slate-700/80">
        <div
          className={`h-full ${BAR_TONES[item.key]}`}
          style={{
            width: `${armed ? donePct : 0}%`,
            transition: `width 0.9s cubic-bezier(0.22, 1, 0.36, 1) ${index * 90}ms`,
          }}
        />
        {partialPct > 0 && (
          <div
            className="h-full bg-amber-500 dark:bg-amber-400"
            style={{
              width: `${armed ? partialPct : 0}%`,
              transition: `width 0.9s cubic-bezier(0.22, 1, 0.36, 1) ${index * 90 + 120}ms`,
            }}
          />
        )}
      </div>
    </div>
  )
}

// ─── leaderboard row ──────────────────────────────────────────────────────

function LeaderboardRow({ row, index, armed }: { row: StatsLeaderboardRow; index: number; armed: boolean }) {
  const { t } = useTranslation()
  const width = armed ? Math.max(0, Math.min(100, row.pct)) : 0
  const tone = row.pct >= 75 ? 'bg-emerald-500 dark:bg-emerald-400'
             : row.pct >= 40 ? 'bg-blue-500 dark:bg-blue-400'
             : 'bg-amber-500 dark:bg-amber-400'
  return (
    <Link
      to={`/projects/${row.id}`}
      className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,2fr)_auto] items-center gap-x-3 rounded px-2 py-1.5 transition-colors hover:bg-slate-100/80 dark:hover:bg-slate-800/60"
    >
      <div className="min-w-0">
        <div className="truncate text-xs font-medium text-slate-900 dark:text-white">{row.hull_number}</div>
        <div className="truncate text-[10px] text-slate-400">
          {row.vessel_name || '—'} · {t('dashboard.growth.projectAchievement', {
            complete: row.complete,
            ra: row.ra_done,
            inventory: row.inv_done,
          })}
        </div>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-200/80 dark:bg-slate-700/80">
        <div
          className={`h-full rounded-full ${tone}`}
          style={{
            width: `${width}%`,
            transition: `width 0.9s cubic-bezier(0.22, 1, 0.36, 1) ${Math.min(index, 8) * 70}ms`,
          }}
        />
      </div>
      <span className="w-11 text-right text-xs font-semibold tabular-nums text-slate-700 dark:text-slate-200">
        {row.pct.toFixed(0)}%
      </span>
    </Link>
  )
}

// ─── charts ───────────────────────────────────────────────────────────────

const fmtTick = (d: string, locale?: string) => {
  const dt = new Date(`${d}T00:00:00`)
  return dt.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
}

const TOOLTIP_STYLE: React.CSSProperties = {
  backgroundColor: 'rgb(15 23 42 / 0.95)', // slate-900 — readable on both themes
  border: '1px solid rgb(51 65 85)',
  borderRadius: 8,
  fontSize: 11,
  color: '#e2e8f0',
  padding: '6px 10px',
}

// ─── the section ──────────────────────────────────────────────────────────

export const StatsSection: React.FC = () => {
  const { t, i18n } = useTranslation()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const armed = useArmed()
  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])

  const load = async () => {
    setLoading(true)
    try {
      const data = await fetchDashboardStats()
      if (mounted.current) { setStats(data); setError(null) }
    } catch (err: any) {
      if (mounted.current) setError(err?.message ?? 'Failed to load stats')
    } finally {
      if (mounted.current) setLoading(false)
    }
  }
  useEffect(() => { void load() }, [])

  // thin the trend for readability on long histories (~90 points max),
  // always keeping the last point so "today" is on the chart
  const trend = useMemo(() => {
    const t = stats?.trend ?? []
    if (t.length <= 90) return t
    const step = Math.ceil(t.length / 90)
    const out = t.filter((_, i) => i % step === 0)
    if (out[out.length - 1] !== t[t.length - 1]) out.push(t[t.length - 1])
    return out
  }, [stats])

  if (error) {
    return (
      <div className="rounded-lg bg-slate-50 p-2.5 dark:bg-slate-800/50">
        <p className="text-[10px] uppercase text-slate-400">{t('dashboard.growth.title')}</p>
        <p className="mt-2 text-[11px] text-slate-500">
          {error}{' '}
          <button type="button" onClick={load} className="text-blue-600 hover:underline dark:text-blue-400">{t('common.tryAgain')}</button>
        </p>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="space-y-2">
        <div className="h-20 animate-pulse rounded-lg bg-slate-50 dark:bg-slate-800/50" />
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-12">
          {[8, 4].map((span, i) => (
            <div key={i} className={`rounded-lg bg-slate-50 p-2.5 dark:bg-slate-800/50 lg:col-span-${span}`}>
              <div className="h-48 animate-pulse rounded bg-slate-100 dark:bg-slate-700/50" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const { hero, by_type, week } = stats
  const leaderboard = [...stats.leaderboard].sort((a, b) =>
    b.pct - a.pct || b.complete - a.complete || a.hull_number.localeCompare(b.hull_number))
  const wordRow = by_type.find(b => b.key === 'word')
  const raRow = by_type.find(b => b.key === 'ra')
  const invRow = by_type.find(b => b.key === 'inventory')
  const excelRow = by_type.find(b => b.key === 'excel')
  // cumulative assessments ingested (the growth line's last point — only grows)
  const assessedTotal = trend.length > 0 ? trend[trend.length - 1].systems : 0
  const locale = i18n.resolvedLanguage === 'ko' ? 'ko-KR' : 'en-US'

  return (
    <div className="space-y-2">

      {/* hero: cumulative achievement — these numbers only ever grow */}
      <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-800/50">
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-[10px] uppercase text-slate-400">{t('dashboard.growth.title')}</p>
          <button
            type="button" onClick={load} disabled={loading} title={t('common.refresh')}
            className="inline-flex h-6 shrink-0 items-center rounded-md border border-slate-200 px-1.5 text-xs hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-700"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="flex flex-wrap items-end gap-x-8 gap-y-2">
          <BigStat value={hero.distinct_systems} label={t('dashboard.growth.systemModels')} accent />
          <BigStat value={hero.installations} label={t('dashboard.growth.installations')} accent />
          <BigStat value={hero.vessels} label={t('dashboard.growth.vessels')} />
          <BigStat value={assessedTotal} label={t('dashboard.growth.assessments')} />
          {/* momentum — the pride signal, promoted from the old footer */}
          <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
            <span className="text-[10px] uppercase text-slate-400">{t('dashboard.growth.thisWeek')}</span>
            <span className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
              <Database className="h-3 w-3 text-blue-500" aria-hidden />
              <span className="font-semibold tabular-nums">+{week.systems_7d}</span> {t('dashboard.growth.systemsAssessed')}
            </span>
            <span className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
              <Layers className="h-3 w-3 text-emerald-500" aria-hidden />
              <span className="font-semibold tabular-nums">+{week.details_7d}</span> {t('dashboard.growth.raDetails')}
            </span>
            <span className="text-[10px] tabular-nums text-slate-400">
              {t('dashboard.growth.last30Days')}: +{week.systems_30d} / +{week.details_30d}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 lg:grid-cols-12">

        {/* growth chart — the climbing line IS the achievement story */}
        <div className="rounded-lg bg-slate-50 p-2.5 dark:bg-slate-800/50 lg:col-span-8">
          <p className="mb-1 flex items-center gap-1.5 text-[10px] uppercase text-slate-400">
            <TrendingUp className="h-3 w-3" aria-hidden />
            {t('dashboard.growth.databaseGrowth')}
          </p>

          <div className="h-56">
            {trend.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ top: 6, right: 6, bottom: 0, left: -18 }}>
                  <defs>
                    <linearGradient id="statsSystems" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="statsDetails" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-slate-200 dark:text-slate-700" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={value => fmtTick(value, locale)} tick={{ fontSize: 10, fill: '#94a3b8' }}
                         axisLine={false} tickLine={false} minTickGap={40} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={label => fmtTick(String(label ?? ''), locale)}
                           formatter={(value, name) => [
                             Number(value ?? 0),
                             String(name) === 'systems'
                               ? t('dashboard.growth.systemsAssessed')
                               : t('dashboard.growth.raDetails'),
                           ]} />
                  <Area type="monotone" dataKey="systems" stroke="#3b82f6" strokeWidth={2}
                        fill="url(#statsSystems)" isAnimationActive animationDuration={1100} />
                  <Area type="monotone" dataKey="details" stroke="#10b981" strokeWidth={2}
                        fill="url(#statsDetails)" isAnimationActive animationDuration={1100} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-[11px] text-slate-400">
                {t('dashboard.growth.foundationReady')}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-lg bg-slate-50 p-2.5 dark:bg-slate-800/50 lg:col-span-4">
          <p className="text-[10px] uppercase text-slate-400">{t('dashboard.growth.achievements')}</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div className="rounded-md bg-white p-2 dark:bg-slate-900/40">
              <FileCheck2 className="mb-1 h-4 w-4 text-emerald-500" aria-hidden />
              <p className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">{hero.systems_complete}</p>
              <p className="text-[10px] text-slate-400">{t('dashboard.growth.fullyDocumented')}</p>
            </div>
            <div className="rounded-md bg-white p-2 dark:bg-slate-900/40">
              <Library className="mb-1 h-4 w-4 text-blue-500" aria-hidden />
              <p className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">{wordRow?.n ?? 0}</p>
              <p className="text-[10px] text-slate-400">{t('dashboard.growth.wordRaMapped')}</p>
            </div>
            <div className="rounded-md bg-white p-2 dark:bg-slate-900/40">
              <Database className="mb-1 h-4 w-4 text-violet-500" aria-hidden />
              <p className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">{hero.ra_done}</p>
              <p className="text-[10px] text-slate-400">{t('dashboard.growth.raCompleted')}</p>
            </div>
            <div className="rounded-md bg-white p-2 dark:bg-slate-900/40">
              <Ship className="mb-1 h-4 w-4 text-cyan-500" aria-hidden />
              <p className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">{hero.inv_done}</p>
              <p className="text-[10px] text-slate-400">{t('dashboard.growth.inventoriesCompleted')}</p>
            </div>
          </div>

          <div className="mt-3 space-y-2.5">
            {raRow && (
              <CoverageBar item={raRow} index={0} armed={armed && !loading} partial={stats.ra_partial} />
            )}
            {invRow && <CoverageBar item={invRow} index={1} armed={armed && !loading} />}
            {excelRow && <CoverageBar item={excelRow} index={2} armed={armed && !loading} />}
          </div>
        </div>
      </div>

      {/* leaderboard: strongest projects first */}
      {leaderboard.length > 0 && (
        <div className="rounded-lg bg-slate-50 p-2.5 dark:bg-slate-800/50">
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <p className="text-[10px] uppercase text-slate-400">{t('dashboard.growth.leadingProjects')}</p>
            <span className="text-[10px] tabular-nums text-slate-400">
              {t('dashboard.growth.projectCount', { count: leaderboard.length })}
            </span>
          </div>
          <div className="max-h-56 space-y-0.5 overflow-y-auto pr-1">
            {leaderboard.map((row, i) => (
              <LeaderboardRow key={row.id} row={row} index={i} armed={armed && !loading} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default StatsSection
